import { useEffect, useId, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../../features/auth/AuthContext'
import { normalizeApiError } from '../../lib/apiClient'
import UserAvatar from './UserAvatar'

export default function AccountMenu({ label }: { label?: string }) {
  const { user, logout } = useAuth()
  const [open, setOpen] = useState(false)
  const [pending, setPending] = useState(false)
  const [error, setError] = useState('')
  const container = useRef<HTMLDivElement>(null)
  const trigger = useRef<HTMLButtonElement>(null)
  const navigate = useNavigate()
  const id = useId()
  useEffect(() => {
    if (!open) return
    const outside = (event: PointerEvent) => { if (!container.current?.contains(event.target as Node)) setOpen(false) }
    document.addEventListener('pointerdown', outside)
    return () => document.removeEventListener('pointerdown', outside)
  }, [open])
  if (!user) return null
  const roles = (['admin', 'doctor', 'patient'] as const).filter(role => user.roles.includes(role))
  const titles = { admin: 'Admin panel', doctor: 'Doctor panel', patient: 'Patient panel' }
  async function signOut() {
    setPending(true); setError('')
    try { await logout(); setOpen(false); navigate('/login') } catch (reason) { setError(normalizeApiError(reason).message) } finally { setPending(false) }
  }
  return <div className="account-menu" ref={container} onKeyDown={event => { if (event.key === 'Escape') { setOpen(false); trigger.current?.focus() } }}>
    <button type="button" ref={trigger} className="profile-button" aria-label={`Account: ${user.name}`} aria-expanded={open} aria-controls={id} onClick={() => setOpen(value => !value)}>
      <UserAvatar name={user.name} /><span className="profile-copy"><strong>{user.name}</strong><small>{label || 'Signed in'}</small></span><i className="bi bi-chevron-down" aria-hidden="true" />
    </button>
    {open && <div className="account-dropdown" id={id} aria-label="Account actions">
      {roles.map(role => <Link key={role} to={`/${role}/dashboard`} onClick={() => setOpen(false)}>{titles[role]}</Link>)}
      <Link to="/" onClick={() => setOpen(false)}>Visit website</Link>
      <button type="button" disabled={pending} onClick={() => void signOut()}>{pending ? 'Logging out…' : 'Logout'}</button>
      {error && <p role="alert">{error}</p>}
    </div>}
  </div>
}
